import styled from '@emotion/styled'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Sex } from '../../../../firebase/firestore/Sex'
import { VoiceChat } from '../../../../firebase/firestore/VoiceChats'
import { ColorSelector } from '@/components/atoms/ColorSelector'
import { PlatformSelector } from '@/components/atoms/PlatformSelector'
import { InputText, InputSelector, OptionsPlaytime } from '@/components/atoms/Inputs'
import { ImageSelector } from '@/components/atoms/ImageSelector'
import { EditorV2Card } from './EditorV2Card'
import { Platform } from '@/clients/firebase/firestore/Platform'
import { gameTitleInfos } from '@/clients/models/types/GameTitle'
import { ProfileEditorAppBanner } from '../../Banner/ProfileEditorAppBanner'
import { FONT_FAMILY, FONT_FAMILY_VALUE } from '@/clients/models/types/Font'
import { useLocale } from '@/hooks/useLocale'
import { colors } from '@/styles/theme'
import { format } from 'date-fns'
import { ProfileCardEditorDescription } from '@/components/atoms/ProfileCardEditorDescription'

// 兼テンプレートID
const HTML_TO_CANVAS_TARGET_ID = 'simpleTemplate'

// フォントの色
const FONT_COLORS = [
  '#FFFFFF', // 白
  '#212121', // ほぼ黒
  '#FF5252', // モダンな赤
  '#FF9800', // モダンなオレンジ
  '#4CAF50', // モダンな緑
  '#03A9F4', // モダンな水色
  '#7E57C2', // モダンな紫
  '#607D8B', // ブルーグレー
]

// 背景色
const BACKGROUND_COLORS = [
  'transparent', // 画像用（透明）
  '#F44336', // モダンな赤
  '#FF9800', // モダンなオレンジ
  '#FFEB3B', // モダンな黄色
  '#4CAF50', // モダンな緑
  '#00BCD4', // モダンな水色
  '#2196F3', // モダンな青
  '#673AB7', // モダンな紫
  '#E91E63', // モダンなピンク
  '#607D8B', // ブルーグレー
]


type FormData = {
  name: string
  id: string
  playTimeWeekDayBegin: number
  playTimeWeekDayEnd: number
  playTimeWeekEndBegin: number
  playTimeWeekEndEnd: number
  playTimeFreeText: string
  playStyle: string
  hitokoto: string
}

type Props = {
  onCreate: (
    data: any,
    targetId: string,
    selectedSex: Sex | 2,
    selectedPlatform: Array<Platform>,
    selectedVoiceChat: Array<VoiceChat>
  ) => void
  submitButtonDisable: boolean
  fonts: FONT_FAMILY[]
  gameTitleId: string
}

export const EditorV2: React.FC<Props> = ({
  onCreate,
  submitButtonDisable,
  gameTitleId,
  fonts,
}) => {
  const { t } = useLocale()
  const { register, watch, handleSubmit } = useForm<FormData>({
    defaultValues: {
      playTimeWeekDayBegin: 18,
      playTimeWeekDayEnd: 20,
      playTimeWeekEndBegin: 18,
      playTimeWeekEndEnd: 20,
      playTimeFreeText: '',
      playStyle: '',
    }
  })
  
  const formValues = watch()
  
  const [selectedPlatform, setSelectedPlatform] = useState<Array<Platform>>([])
  const [selectedBackgroundColor, setSelectedBackgroundColor] = useState(0) 
  const [selectedFontColor, setSelectedFontColor] = useState(0) 
  const [selectedFontFamily, setSelectedFontFamily] = useState<FONT_FAMILY_VALUE>('notosans')

  const onSubmit = (data: FormData) => {
    if (submitButtonDisable) {
      return
    }
    onCreate(
      data,
      HTML_TO_CANVAS_TARGET_ID,
      2,
      selectedPlatform,
      [],
    )
  }

  return (
    <Container>
      <PreviewContainer>
        <EditorV2Card
          targetId={HTML_TO_CANVAS_TARGET_ID}
          backgroundColor={selectedBackgroundColor === 0 ? 'image' : BACKGROUND_COLORS[selectedBackgroundColor]}
          fontColor={FONT_COLORS[selectedFontColor]}
          fontFamily={selectedFontFamily}
          name={formValues.name}
          id={formValues.id}
          platform={selectedPlatform}
          playTimeWeekDayBegin={formValues.playTimeWeekDayBegin}
          playTimeWeekDayEnd={formValues.playTimeWeekDayEnd}
          playTimeWeekEndBegin={formValues.playTimeWeekEndBegin}
          playTimeWeekEndEnd={formValues.playTimeWeekEndEnd}
          playTimeFreeText={formValues.playTimeFreeText}
          playStyle={formValues.playStyle}
          hitokoto={formValues.hitokoto}
          titleJa={gameTitleInfos[gameTitleId]?.titleJa}
          coverImageUrl={gameTitleInfos[gameTitleId]?.coverImgUrl}
        />
        <ProfileEditorAppBanner  />
      </PreviewContainer>

      <DescriptionSection>
        <Time>
          <time dateTime={new Date().toISOString()} itemProp="datepublished" />
          <time dateTime={new Date().toISOString()} itemProp="modified">
            {t.最終更新}: {format(new Date(), 'yyyy年MM月dd日')}
          </time>
        </Time>
        <H1Text>{`【${gameTitleInfos[gameTitleId]?.titleJa}】自己紹介カード作成ツール` || ''} {t.かわいい自己紹介カード作成ツール}</H1Text>
        <H2Text>
          {`${gameTitleInfos[gameTitleId]?.titleJa}のかわいい自己紹介カードが簡単に作成・編集できるツールです！🥳🎉 自分だけの自己紹介カードが簡単に作成できます。プレビューを見ながら文字入れをしてあなただけの自己紹介カードをつくろう！`}
          </H2Text>
        <ProfileCardEditorDescription />
      </DescriptionSection>

      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormItemContainer>
            <SectionHeader>カードの背景</SectionHeader>
            <ColorSelector
              colors={BACKGROUND_COLORS}
              value={selectedBackgroundColor}
              onChange={(index) => setSelectedBackgroundColor(index)}
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>フォントの色</SectionHeader>
            <ColorSelector
              colors={FONT_COLORS}
              value={selectedFontColor}
              onChange={(index) => setSelectedFontColor(index)}
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>フォント</SectionHeader>
            <ImageSelector
              values={fonts}
              value={selectedFontFamily}
              onChange={(value) => setSelectedFontFamily(value as FONT_FAMILY_VALUE)}
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>なまえ</SectionHeader>
            <InputText {...register('name')} placeholder="" />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>ID</SectionHeader>
            <InputText {...register('id')} placeholder="ゲームID" />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>プラットフォーム</SectionHeader>
            <PlatformSelector
              onChange={(platforms) => setSelectedPlatform(platforms)}
              value={selectedPlatform}
              activeValues={[0, 1, 2, 3, 4]} // すべてのプラットフォームを有効化
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>プレイ時間（平日）</SectionHeader>
            <InputSelector {...register('playTimeWeekDayBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekDayEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>プレイ時間（休日）</SectionHeader>
            <InputSelector {...register('playTimeWeekEndBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekEndEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>プレイ時間（自由記述）</SectionHeader>
            <InputText {...register('playTimeFreeText')} placeholder="不定期" />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>プレイスタイル</SectionHeader>
            <InputText {...register('playStyle')} placeholder="エンジョイ" />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>ひとこと</SectionHeader>
            <InputText
              {...register('hitokoto')}
              placeholder="よろしくお願いします！"
            />
          </FormItemContainer>

          <ButtonDescriptionText>
            {t.カードの作成にはSNS連携が必要です次の画面でログインをします}
          </ButtonDescriptionText>
          <Submit
            id="create_profile"
            type="submit"
            value={t.これでカードをつくる}
            disabled={submitButtonDisable}
          />
        </form>
      </FormContainer>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding-bottom: 40px;
`

const PreviewContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  margin-bottom: 20px;
`

const FormContainer = styled.div`
  padding: 16px;
`

const FormItemContainer = styled.div`
  margin-bottom: 24px;
`

const SectionHeader = styled.div`
  margin-bottom: 12px;
  font-weight: bold;
  font-size: 16px;
  color: #333;
`

const ButtonDescriptionText = styled.h3`
  margin: auto;
  margin-top: 30px;
  width: 100%;
  font-size: 10px;
  color: ${colors.text.secondary};
  justify-content: center;
  display: flex;
`

const Submit = styled.input`
  margin-top: 12px;
  border-radius: 20px;
  border: 3px solid transparent;
  cursor: pointer;
  width: 100%;
  height: 40px;
  background-color: ${colors.button.primary};
  color: #fff;
  font-weight: bold;
  display: block;
`

const TimeSelectorText = styled.span`
  display: block;
  text-align: center;
  margin: 8px 0;
  font-weight: bold;
  color: #333;
`

const DescriptionSection = styled.label``

const Time = styled.div`
  color: ${colors.text.secondary};
  font-size: 12px;
`

const H1Text = styled.h1`
  margin-top: 24px;
  font-weight: bold;
  color: ${colors.text.primary};
  text-align: center;
  display: flex;
  line-height: 1.5;
`

const H2Text = styled.h2`
  margin-top: 8px;
  font-size: 11px;
  color: ${colors.text.secondary};
  line-height: 1.5;
  display: flex;
`